import type { JobCalcsPreviewResponse } from "@stryker/core/types/jobs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Typography } from "@/components/ui/typography";

type FinalDesignPreviewModalProps = {
  open: boolean;
  preview: JobCalcsPreviewResponse | null;
  isLoading: boolean;
  onClose: () => void;
  onContinue: () => void;
};

export function FinalDesignPreviewModal({
  open,
  preview,
  isLoading,
  onClose,
  onContinue,
}: FinalDesignPreviewModalProps) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogTitle>Final Design</DialogTitle>
        <DialogDescription className="sr-only">
          Review the final design preview before continuing with calculations.
        </DialogDescription>
        <div className="md md:max-h-auto flex max-h-[70vh] flex-col gap-4 overflow-auto">
          <Typography variant="text-lg" weight="semibold">
            Design Preview
          </Typography>

          {isLoading && (
            <Typography variant="text-small" className="text-gray-500">
              Generating preview...
            </Typography>
          )}

          {!isLoading && preview?.error && (
            <Typography variant="text-small" className="text-destructive">
              Error generating preview: {preview.error}
            </Typography>
          )}

          {!isLoading && preview?.previews?.length ? (
            <div className="flex flex-col gap-3">
              {preview.previews.length > 1 ? (
                <Typography variant="text-small" className="text-gray-500">
                  Scroll down to view all previews.
                </Typography>
              ) : null}
              {preview.previews.map((previewImage, index) => (
                <div key={`${previewImage}-${index}`} className="rounded-md border border-solid border-success-500 p-3">
                  <img
                    src={`data:image/png;base64,${previewImage}`}
                    alt={`Design Preview ${index + 1}`}
                    className="h-auto max-w-full"
                  />
                </div>
              ))}
            </div>
          ) : null}

          {!isLoading && !preview?.previews?.length && !preview?.error && (
            <Typography variant="text-small" className="text-gray-500">
              No preview available.
            </Typography>
          )}

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button className="flex-1" disabled={isLoading} onClick={onContinue}>
              Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
